import { useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import type { AgentName, InvokeResponseBase } from '../../lib/types';

interface AgentMeta {
  title: string;
  module: string;
  model: string;
  description: string;
  sample_payload: Record<string, unknown>;
}

const META: Partial<Record<AgentName, AgentMeta>> = {
  fiscaliste: {
    title: 'Fiscaliste',
    module: 'Fiscal · Phase 2',
    model: 'Sonnet 4.6',
    description:
      'Déclarations fiscales tunisiennes — TVA mensuelle (collectée vs déductible), IS annuelle (taux par secteur), RAS périodique.',
    sample_payload: {
      declaration_type: 'tva_mensuelle',
      fiscal_period: '2026-03',
      ventes: [{ amount_ht_millimes: 10_000_000, vat_rate: 19 }],
      achats: [{ amount_ht_millimes: 4_000_000, vat_rate: 19 }],
      credit_tva_reporte_millimes: 0,
    },
  },
  clotureur: {
    title: 'Clôturier',
    module: 'Comptabilité · Phase 2',
    model: 'Opus 4.7',
    description:
      'Pilote la clôture annuelle 12 étapes — collecte, amortissements, IS, états financiers, liasse fiscale.',
    sample_payload: {
      fiscal_year: 2026,
      fiscal_year_start: '2026-01-01',
      fiscal_year_end: '2026-12-31',
      company_name: 'Sahel Textile Industries',
      immobilisations: [
        {
          asset_id: 'IMMO-1',
          acquisition_date: '2024-01-01',
          acquisition_value_millimes: 10_000_000,
          duration_years: 5,
          method: 'linear',
        },
      ],
      taxable_income_before_amort_millimes: 50_000_000,
    },
  },
  liassier: {
    title: 'Liassier',
    module: 'Audit · Phase 2',
    model: 'Sonnet 4.6',
    description:
      'Pré-remplissage de la liasse fiscale tunisienne — annexes 1 (bilan) & 2 (compte de résultat), contrôles inter-annexes.',
    sample_payload: {
      fiscal_year: 2026,
      company_name: 'Sahel Textile',
      company_matricule_fiscal: '1234567A/M/A/000',
      trial_balance: [
        { sce_code: '101', account_class: 1, debit_total_millimes: 0, credit_total_millimes: 50_000_000 },
        { sce_code: '213', account_class: 2, debit_total_millimes: 30_000_000, credit_total_millimes: 0 },
        { sce_code: '411', account_class: 4, debit_total_millimes: 20_000_000, credit_total_millimes: 0 },
        { sce_code: '532', account_class: 5, debit_total_millimes: 15_000_000, credit_total_millimes: 0 },
        { sce_code: '401', account_class: 4, debit_total_millimes: 0, credit_total_millimes: 5_000_000 },
        { sce_code: '607', account_class: 6, debit_total_millimes: 30_000_000, credit_total_millimes: 0 },
        { sce_code: '707', account_class: 7, debit_total_millimes: 0, credit_total_millimes: 40_000_000 },
      ],
      annexes_to_include: [1, 2],
    },
  },
  controleur_dgi: {
    title: 'Contrôleur DGI ★',
    module: 'Fiscal · Phase 3',
    model: 'Opus 4.7',
    description:
      "Audit fiscal complet sur 7 axes — journal SCE, IS, déductibilité, CCA, recettes (TTN ↔ 70x), TVA, immobilisations, RAS.",
    sample_payload: {
      fiscal_year: 2026,
      company_name: 'Sahel Textile',
      sce_entries: [
        {
          id: 'e1',
          sequence_number: 1,
          entry_date: '2026-04-15',
          fiscal_year: 2026,
          debit_account: '618',
          credit_account: '532',
          amount_millimes: 500_000,
          label: 'Amende pour retard de paiement',
        },
      ],
      ttn_invoices: [],
      revenue_accounts_total_millimes: 0,
    },
  },
  veilleur_jort: {
    title: 'Veilleur JORT',
    module: 'Fiscal · Phase 3',
    model: 'Opus 4.7',
    description:
      "Veille réglementaire Journal Officiel Tunisien — décrets, arrêtés, lois de finances, scoring de pertinence pour PME.",
    sample_payload: {
      from_date: '2026-03-01',
      to_date: '2026-05-31',
      domain: 'fiscal',
      min_relevance: 0.5,
    },
  },
  optimisateur: {
    title: 'Optimisateur',
    module: 'Fiscal · Phase 3',
    model: 'Opus 4.7',
    description:
      "Analyse what-if — comparaison de scénarios d'optimisation IS (dotations, reclassements sectoriels, crédits d'impôt).",
    sample_payload: {
      fiscal_year: 2026,
      fiscal_year_start: '2026-01-01',
      fiscal_year_end: '2026-12-31',
      baseline_taxable_income_millimes: 100_000_000,
      scenarios: [
        {
          scenario_id: 'S1',
          label: 'Investissement R&D + 10M dotation',
          taxable_income_delta_millimes: -10_000_000,
          rationale: "Crédit d'impôt R&D selon Loi de Finances 2026",
        },
        {
          scenario_id: 'S2',
          label: 'Reclassement industrie export',
          taxable_income_delta_millimes: 0,
          sector_code_override: 'industrie_export',
          rationale: 'Activité éligible au régime industriel exportateur (10%)',
        },
      ],
    },
  },
  cac_oect: {
    title: 'CAC OECT',
    module: 'Audit · Phase 4',
    model: 'Sonnet 4.6',
    description:
      "Pré-remplissage rapport Commissaire aux Comptes — opinion (sans réserve / avec réserve / défavorable / refus), ratios financiers, zones d'audit.",
    sample_payload: {
      fiscal_year: 2026,
      company_name: 'Sahel Textile',
      sce_entries: [],
      ttn_invoices: [],
      revenue_accounts_total_millimes: 0,
      financials: {
        ca_ht_millimes: 100_000_000,
        achats_millimes: 60_000_000,
        resultat_net_millimes: 15_000_000,
        capitaux_propres_millimes: 50_000_000,
        creances_clients_millimes: 20_000_000,
        dettes_fournisseurs_millimes: 10_000_000,
      },
    },
  },
  drh_cnss: {
    title: 'DRH/CNSS',
    module: 'Extension · Phase 4',
    model: 'Sonnet 4.6',
    description:
      "Paie + déclarations sociales tunisiennes — CNSS (employé/employeur), TFP, Foprolos, RAS honoraires consultants.",
    sample_payload: {
      fiscal_period: '2026-04',
      employes: [
        { employe_id: 'E1', full_name: 'Mohamed Ben Salem', status: 'salarie', gross_monthly_millimes: 2_000_000 },
        { employe_id: 'C1', full_name: 'Consultant Externe', status: 'consultant_freelance', gross_monthly_millimes: 5_000_000 },
      ],
    },
  },
  juriste_rne: {
    title: 'Juriste RNE',
    module: 'Extension · Phase 4',
    model: 'Sonnet 4.6',
    description:
      "Actes juridiques tunisiens pré-remplis — PV AGO, statuts SARL, décisions du gérant SUARL, citations COC + Code des sociétés commerciales.",
    sample_payload: {
      template_id: 'pv_ago',
      societe_name: 'Sahel Textile Industries',
      societe_matricule_fiscal: '1234567A/M/A/000',
      societe_address: '15 rue Sousse, Sahel',
      capital_millimes: 250_000_000,
      generation_date: '2026-05-04',
      fiscal_year: 2025,
      resultat_net_millimes: 50_000_000,
      affectation: 'reserve_legale_partielle',
      associes: [
        { nom: 'Mohamed Ben Salem', parts: 600 },
        { nom: 'Fatma Trabelsi', parts: 400 },
      ],
    },
  },
  tresorier: {
    title: 'Trésorier',
    module: 'Extension · Phase 4',
    model: 'Sonnet 4.6',
    description:
      "Projection cashflow rolling 13 semaines ou 12 mois, alertes solde + DSO si receivables fournis.",
    sample_payload: {
      bank_account_sce_code: '532',
      starting_balance_millimes: 5_000_000,
      starting_date: '2026-05-04',
      horizon: '13w',
      inflows: [
        { id: 'in1', expected_date: '2026-05-15', amount_millimes: 2_000_000, label: 'Client ACME', certain: true },
      ],
      outflows: [
        { id: 'out1', expected_date: '2026-05-10', amount_millimes: 1_500_000, label: 'Fournisseur', certain: true },
        { id: 'out2', expected_date: '2026-06-28', amount_millimes: 3_000_000, label: 'Acompte IS', certain: true },
      ],
      alert_threshold_millimes: 0,
      receivables: { ca_ht_millimes: 100_000_000, creances_clients_millimes: 25_000_000 },
    },
  },
};

const ALL_AGENTS: AgentName[] = Object.keys(META) as AgentName[];

export function AgentJsonInvokePage() {
  const { agentName: rawName = '' } = useParams();
  const agentName = rawName as AgentName;
  const meta = META[agentName];

  const initialPayload = useMemo(
    () => (meta ? JSON.stringify(meta.sample_payload, null, 2) : '{}'),
    [meta],
  );
  const [payloadText, setPayloadText] = useState(initialPayload);
  const [parseError, setParseError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  if (!meta) {
    if (!ALL_AGENTS.includes(agentName)) {
      return <Navigate to="/" replace />;
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setParseError(null);
    setResponse(null);
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(payloadText);
    } catch (err) {
      setParseError(`JSON invalide : ${(err as Error).message}`);
      setLoading(false);
      return;
    }
    try {
      const res = await api.invokeAgent(agentName, {
        payload,
        fiscal_reference_date: new Date().toISOString().slice(0, 10),
      });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  if (!meta) {
    return (
      <AgentPageShell
        title="Agent inconnu"
        module=""
        model=""
        description={<>L'agent <code>{agentName}</code> n'est pas reconnu.</>}
      >
        <></>
      </AgentPageShell>
    );
  }

  return (
    <AgentPageShell
      title={meta.title}
      module={meta.module}
      model={meta.model}
      description={meta.description}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard
          title="Payload JSON"
          action={
            <button
              type="button"
              onClick={() => setPayloadText(JSON.stringify(meta.sample_payload, null, 2))}
              className="text-xs px-2 py-1 rounded bg-white border border-border hover:border-gold transition"
            >
              Réinitialiser exemple
            </button>
          }
        >
          <Field
            label="Corps de la requête"
            hint="L'agent valide via Zod. Les champs structurés varient selon l'agent — voir docs/02-agents-catalog.md ou le sample."
          >
            <textarea
              rows={20}
              className={inputCls + ' font-mono text-xs'}
              value={payloadText}
              onChange={(e) => setPayloadText(e.target.value)}
              spellCheck={false}
              required
            />
          </Field>
          {parseError && <div className="text-xs text-red-700">{parseError}</div>}
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Invocation…' : `Invoquer ${meta.title}`}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <JsonView value={result} />}
        />
      )}
    </AgentPageShell>
  );
}

function JsonView({ value }: { value: unknown }) {
  return (
    <pre className="font-mono text-xs whitespace-pre-wrap bg-cream/40 border border-border rounded p-3 leading-relaxed max-h-[600px] overflow-auto">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
